import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import {
  ChevronLeft, Plus, Brain, Camera, Activity, AlertTriangle,
  X, Footprints, Shield, ArrowLeftRight, ZoomIn, ChevronRight,
  ChevronLeft as ChevLeft, Calendar, Layers, Clock, FileDown
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WOUND_LABELS = { ulcera_venosa: "Úlcera Venosa", pe_diabetico: "Pé Diabético", lesao_por_pressao: "Lesão por Pressão" };
const RISK_LABELS = { critico: "Crítico", alto: "Alto", medio: "Médio", baixo: "Baixo" };
const STATUS_LABELS = { ativo: "Ativo", cicatrizado: "Cicatrizado", encaminhado: "Encaminhado", amputado: "Amputado" };

const WAGNER_GRADES = [
  {
    grade: 0,
    label: "Grau 0 — Pé em Risco",
    description: "Sem úlcera. Deformidade óssea, calosidade ou neuropatia presentes.",
    color: "bg-green-100 border-green-300 text-green-800",
    badge: "bg-green-500",
    action: "Cuidados preventivos: inspeção diária, calçado adequado, hidratação.",
  },
  {
    grade: 1,
    label: "Grau 1 — Úlcera Superficial",
    description: "Perda cutânea superficial. Não envolve estruturas profundas.",
    color: "bg-yellow-100 border-yellow-300 text-yellow-800",
    badge: "bg-yellow-500",
    action: "Curativo úmido, desbridamento, controle glicêmico rigoroso.",
  },
  {
    grade: 2,
    label: "Grau 2 — Úlcera Profunda",
    description: "Atinge tendão, cápsula articular ou periósteo. Sem abscesso ou osteomielite.",
    color: "bg-orange-100 border-orange-300 text-orange-800",
    badge: "bg-orange-500",
    action: "Desbridamento cirúrgico, antibioticoterapia profilática, avaliar vascularização.",
  },
  {
    grade: 3,
    label: "Grau 3 — Úlcera Profunda + Infecção",
    description: "Abscesso profundo, osteomielite ou artrite séptica.",
    color: "bg-red-100 border-red-300 text-red-800",
    badge: "bg-red-500",
    action: "INTERNAÇÃO: debridamento, antibioticoterapia EV, avaliar cirurgia óssea.",
  },
  {
    grade: 4,
    label: "Grau 4 — Gangrena Localizada",
    description: "Necrose de dedo(s) ou antepé.",
    color: "bg-red-200 border-red-400 text-red-900",
    badge: "bg-red-700",
    action: "URGÊNCIA: amputação parcial, revascularização se possível.",
  },
  {
    grade: 5,
    label: "Grau 5 — Gangrena Extensa",
    description: "Necrose extensa envolvendo todo o pé.",
    color: "bg-gray-200 border-gray-400 text-gray-900",
    badge: "bg-gray-700",
    action: "EMERGÊNCIA: amputação maior, avaliação vascular e de UTI.",
  },
];

const NPUAP_STAGES = [
  { stage: "1", label: "Estágio 1 — Eritema Não Branqueável", description: "Pele intacta com eritema não branqueável. Pode ser precursor de lesão mais grave.", color: "bg-yellow-100 border-yellow-300 text-yellow-800", badge: "bg-yellow-500", action: "Alívio imediato da pressão. Reposicionamento 2/2h. Coxins de proteção." },
  { stage: "2", label: "Estágio 2 — Perda Parcial da Espessura", description: "Perda de espessura parcial com derme exposta. Ferida úmida rosada/vermelha. Sem esfacelo.", color: "bg-orange-100 border-orange-300 text-orange-800", badge: "bg-orange-500", action: "Curativo hidrocoloide ou espuma. Alívio de pressão. Controle de umidade." },
  { stage: "3", label: "Estágio 3 — Perda Total da Espessura", description: "Perda total da espessura. Gordura subcutânea visível. Pode ter esfacelo ou escara.", color: "bg-red-100 border-red-300 text-red-800", badge: "bg-red-500", action: "Desbridamento se necessário. Curativos avançados. Suporte nutricional intensivo." },
  { stage: "4", label: "Estágio 4 — Perda Total + Tecido Exposto", description: "Exposição de osso, tendão, músculo ou cápsula articular. Alto risco de osteomielite.", color: "bg-red-200 border-red-400 text-red-900", badge: "bg-red-700", action: "INTERNAÇÃO urgente. Cirurgia reconstrutiva, antibioticoterapia EV, equipe multidisciplinar." },
  { stage: "nao_classificavel", label: "Não Classificável — Profundidade Obscurecida", description: "Perda total de espessura com esfacelo ou escara cobrindo a profundidade real.", color: "bg-gray-100 border-gray-400 text-gray-800", badge: "bg-gray-600", action: "Desbridamento para reclassificação. Não remover escara calcânea estável." },
  { stage: "dti", label: "Lesão de Tecido Profundo (DTI)", description: "Descoloração vermelha escura/marrom/púrpura persistente. Pode evoluir rapidamente.", color: "bg-purple-100 border-purple-300 text-purple-800", badge: "bg-purple-600", action: "Alívio imediato de pressão. Monitoramento frequente. Pode evoluir para estágio 3 ou 4." },
];

function NPUAPTab({ wound, onStageUpdate }) {
  const [saving, setSaving] = useState(false);
  const current = wound.npuap_stage || null;

  const handleSet = async (stage) => {
    setSaving(true);
    try {
      const resp = await axios.put(`${API}/wounds/${wound.wound_id}`, { npuap_stage: stage }, { withCredentials: true });
      onStageUpdate(resp.data.npuap_stage);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-border">
        <Shield className="w-4 h-4 text-primary" />
        <div>
          <p className="text-sm font-semibold text-foreground">Classificação NPUAP/EPUAP</p>
          <p className="text-xs text-muted-foreground">Sistema internacional de estadiamento de lesões por pressão</p>
        </div>
      </div>

      {current && (
        <div className={`flex items-center gap-3 p-3 rounded-xl border-2 ${NPUAP_STAGES.find(s => s.stage === current)?.color || ""}`} data-testid="current-npuap-stage">
          <div className={`w-10 h-10 rounded-full ${NPUAP_STAGES.find(s => s.stage === current)?.badge} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>
            {current === "nao_classificavel" ? "NC" : current === "dti" ? "DTI" : current}
          </div>
          <div>
            <p className="text-sm font-bold">{NPUAP_STAGES.find(s => s.stage === current)?.label}</p>
            <p className="text-xs mt-0.5">{NPUAP_STAGES.find(s => s.stage === current)?.action}</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {NPUAP_STAGES.map(({ stage, label, description, color, badge }) => (
          <button key={stage} onClick={() => handleSet(stage)} disabled={saving}
            className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-all ${
              current === stage ? `${color} border-2` : "border-border hover:bg-secondary/50"
            }`}
            data-testid={`npuap-stage-${stage}`}
          >
            <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold transition-all ${current === stage ? `${badge} text-white` : "bg-muted text-foreground"}`}>
              {stage === "nao_classificavel" ? "NC" : stage === "dti" ? "DTI" : stage}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground">{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
            </div>
            {current === stage && <span className="text-xs font-medium text-primary flex-shrink-0">Atual</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function AreaTimeline({ evolutions }) {
  if (!evolutions?.length) return null;
  const data = evolutions
    .filter(e => e.length_cm > 0 || e.width_cm > 0)
    .map(e => ({
      date: new Date(e.date || e.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
      area: parseFloat(((e.length_cm || 0) * (e.width_cm || 0)).toFixed(1)),
      comprimento: e.length_cm || 0,
      largura: e.width_cm || 0,
    }))
    .reverse();

  if (data.length < 2) return null;

  return (
    <div className="mt-4 bg-secondary/30 rounded-xl p-4" data-testid="area-timeline-chart">
      <p className="text-xs font-semibold text-foreground mb-3">Evolução da Área (cm²)</p>
      <ResponsiveContainer width="100%" height={120}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} unit="cm²" />
          <Tooltip formatter={(v) => [`${v} cm²`, "Área"]} labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
          <Area type="monotone" dataKey="area" stroke="hsl(var(--primary))" fill="url(#areaGrad)" strokeWidth={2} dot={{ r: 3 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function WagnerTab({ wound, onGradeUpdate }) {
  const [saving, setSaving] = useState(false);
  const currentGrade = wound.wagner_grade ?? null;

  const handleSetGrade = async (grade) => {
    setSaving(true);
    try {
      const resp = await axios.put(`${API}/wounds/${wound.wound_id}`, { wagner_grade: grade }, { withCredentials: true });
      onGradeUpdate(resp.data.wagner_grade);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-border">
        <Footprints className="w-4 h-4 text-primary" />
        <div>
          <p className="text-sm font-semibold text-foreground">Classificação de Wagner</p>
          <p className="text-xs text-muted-foreground">Selecione o grau observado no momento da avaliação</p>
        </div>
      </div>

      {currentGrade !== null && currentGrade !== undefined && (
        <div className={`flex items-center gap-3 p-3 rounded-xl border-2 ${WAGNER_GRADES[currentGrade]?.color}`} data-testid="current-wagner-grade">
          <div className={`w-10 h-10 rounded-full ${WAGNER_GRADES[currentGrade]?.badge} text-white flex items-center justify-center text-lg font-bold flex-shrink-0`}>
            {currentGrade}
          </div>
          <div>
            <p className="text-sm font-bold">{WAGNER_GRADES[currentGrade]?.label}</p>
            <p className="text-xs mt-0.5">{WAGNER_GRADES[currentGrade]?.action}</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {WAGNER_GRADES.map(({ grade, label, description, color, badge }) => (
          <button
            key={grade}
            onClick={() => handleSetGrade(grade)}
            disabled={saving}
            className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-all ${
              currentGrade === grade ? `${color} border-2` : "border-border hover:bg-secondary/50"
            }`}
            data-testid={`wagner-grade-${grade}`}
          >
            <div className={`w-8 h-8 rounded-full ${grade === currentGrade ? badge : "bg-muted"} ${grade === currentGrade ? "text-white" : "text-foreground"} flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all`}>
              {grade}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground">{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
            </div>
            {currentGrade === grade && (
              <span className="text-xs font-medium text-primary flex-shrink-0">Atual</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function PhotoHistoryTimeline({ photos, evolutions, sessionToken }) {
  const [mode, setMode] = useState("timeline"); // "timeline" | "compare"
  const [selectedSession, setSelectedSession] = useState(null); // for lightbox
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [compareA, setCompareA] = useState(0);
  const [compareB, setCompareB] = useState(1);
  const scrollRef = useRef(null);

  const getPhotoUrl = (photo) =>
    `${API}/photos/download/${photo.storage_path}?auth=${sessionToken}`;

  // Group photos by evolution_id, sort by evolution date
  const sessions = (() => {
    const evoMap = {};
    evolutions.forEach((e) => { evoMap[e.evolution_id] = e; });
    const groups = {};
    photos.forEach((p) => {
      const eid = p.evolution_id || "sem_evolucao";
      if (!groups[eid]) groups[eid] = { evolution_id: eid, photos: [], date: null };
      groups[eid].photos.push(p);
      if (!groups[eid].date && evoMap[eid]) groups[eid].date = evoMap[eid].date;
      if (!groups[eid].date) groups[eid].date = p.taken_at?.split("T")[0] || "—";
    });
    return Object.values(groups).sort((a, b) => (a.date > b.date ? 1 : -1));
  })();

  const selectedPhotos = selectedSession?.photos || [];

  if (photos.length === 0) {
    return (
      <div className="text-center py-12" data-testid="photo-history-empty">
        <Camera className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
        <p className="text-sm font-medium text-foreground">Sem fotos registradas</p>
        <p className="text-xs text-muted-foreground mt-1">As fotos aparecerão aqui após o primeiro registro fotográfico na evolução.</p>
      </div>
    );
  }

  return (
    <div data-testid="photo-history-timeline">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {photos.length} foto{photos.length !== 1 ? "s" : ""} · {sessions.length} sessão(ões)
          </p>
          <p className="text-xs text-muted-foreground">Toque numa sessão para ampliar</p>
        </div>
        {sessions.length >= 2 && (
          <button
            onClick={() => setMode(mode === "compare" ? "timeline" : "compare")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              mode === "compare"
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:bg-secondary"
            }`}
            data-testid="toggle-compare-btn"
          >
            <ArrowLeftRight className="w-3.5 h-3.5" />
            {mode === "compare" ? "Ver timeline" : "Comparar"}
          </button>
        )}
      </div>

      {/* ===== TIMELINE MODE ===== */}
      {mode === "timeline" && (
        <div>
          {/* Horizontal scrollable session strip */}
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth"
            style={{ scrollbarWidth: "thin" }}
          >
            {sessions.map((session, i) => {
              const cover = session.photos[0];
              const formatted = session.date
                ? new Date(session.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
                : "—";
              return (
                <button
                  key={session.evolution_id}
                  onClick={() => { setSelectedSession(session); setLightboxIdx(0); }}
                  className="flex-shrink-0 snap-start w-28 group"
                  data-testid={`session-thumb-${i}`}
                >
                  <div className="relative rounded-xl overflow-hidden aspect-square bg-muted border border-border group-hover:border-primary transition-all group-hover:scale-105">
                    <img
                      src={getPhotoUrl(cover)}
                      alt={`Sessão ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {session.photos.length > 1 && (
                      <div className="absolute top-1.5 right-1.5 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        +{session.photos.length - 1}
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent pb-1.5 pt-3 px-1.5">
                      <p className="text-white text-[10px] font-medium text-center">{formatted}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Sessions list with full details */}
          <div className="mt-5 space-y-4">
            {sessions.map((session, i) => {
              const formatted = session.date
                ? new Date(session.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
                : "Data desconhecida";
              return (
                <div key={session.evolution_id} className="border border-border rounded-xl overflow-hidden" data-testid={`session-detail-${i}`}>
                  <div className="flex items-center justify-between px-4 py-2.5 bg-secondary/30 border-b border-border">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-primary" />
                      <span className="text-xs font-semibold text-foreground">{formatted}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{session.photos.length} foto{session.photos.length > 1 ? "s" : ""}</span>
                  </div>
                  <div className={`grid gap-1 p-2 ${session.photos.length === 1 ? "grid-cols-1" : session.photos.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
                    {session.photos.map((photo, pi) => (
                      <button
                        key={photo.photo_id}
                        onClick={() => { setSelectedSession(session); setLightboxIdx(pi); }}
                        className="relative aspect-square rounded-lg overflow-hidden bg-muted group"
                        data-testid={`session-photo-${i}-${pi}`}
                      >
                        <img
                          src={getPhotoUrl(photo)}
                          alt={photo.notes || `Foto ${pi + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {photo.notes && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-3">
                            <p className="text-white text-[10px] text-center">{photo.notes}</p>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <ZoomIn className="w-6 h-6 text-white drop-shadow" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== COMPARE MODE ===== */}
      {mode === "compare" && sessions.length >= 2 && (
        <div data-testid="compare-mode">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Sessão A", idx: compareA, setIdx: setCompareA },
              { label: "Sessão B", idx: compareB, setIdx: setCompareB },
            ].map(({ label, idx, setIdx }, col) => {
              const session = sessions[idx] || sessions[0];
              const formatted = session.date
                ? new Date(session.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "2-digit" })
                : "—";
              return (
                <div key={col}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${col === 0 ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>{label}</span>
                    <select
                      value={idx}
                      onChange={e => setIdx(Number(e.target.value))}
                      className="text-xs border border-border rounded-lg px-1.5 py-1 bg-white max-w-[120px]"
                      data-testid={`compare-select-${col}`}
                    >
                      {sessions.map((s, si) => {
                        const d = s.date
                          ? new Date(s.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "2-digit" })
                          : `Sessão ${si + 1}`;
                        return <option key={si} value={si}>{d}</option>;
                      })}
                    </select>
                  </div>
                  {/* Show photos stacked vertically for the session */}
                  <div className="space-y-1.5">
                    {session.photos.map((photo, pi) => (
                      <div key={photo.photo_id} className="relative rounded-xl overflow-hidden aspect-square bg-muted border border-border" data-testid={`compare-photo-${col}-${pi}`}>
                        <img
                          src={getPhotoUrl(photo)}
                          alt={photo.notes || `Foto ${pi + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {photo.notes && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-3">
                            <p className="text-white text-[10px] text-center">{photo.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-1.5 text-center">
                    <p className="text-xs font-medium text-muted-foreground">{formatted}</p>
                    <p className="text-xs text-muted-foreground">{session.photos.length} foto(s)</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Delta indicator */}
          {sessions[compareA]?.date && sessions[compareB]?.date && (
            <div className="mt-4 bg-secondary/50 rounded-xl p-3 text-center border border-border">
              <div className="flex items-center justify-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {Math.abs(
                    Math.round(
                      (new Date(sessions[compareB].date) - new Date(sessions[compareA].date)) / (1000 * 60 * 60 * 24)
                    )
                  )} dias de diferença entre as sessões
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== LIGHTBOX ===== */}
      {selectedSession && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={e => e.target === e.currentTarget && setSelectedSession(null)}
          data-testid="photo-lightbox"
        >
          <button
            onClick={() => setSelectedSession(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all"
            data-testid="lightbox-close-btn"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Date + incidence */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-center">
            <p className="text-white/80 text-xs font-medium">
              {selectedSession.date
                ? new Date(selectedSession.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
                : "—"}
            </p>
            {selectedPhotos[lightboxIdx]?.notes && (
              <p className="text-white/60 text-xs mt-0.5">{selectedPhotos[lightboxIdx].notes}</p>
            )}
          </div>

          {/* Main image */}
          <div className="relative max-w-xl w-full px-10">
            <img
              src={getPhotoUrl(selectedPhotos[lightboxIdx])}
              alt={selectedPhotos[lightboxIdx]?.notes || "Foto"}
              className="w-full rounded-xl max-h-[70vh] object-contain"
              data-testid="lightbox-main-image"
            />

            {/* Prev/next */}
            {selectedPhotos.length > 1 && (
              <>
                <button
                  onClick={() => setLightboxIdx(i => (i - 1 + selectedPhotos.length) % selectedPhotos.length)}
                  disabled={lightboxIdx === 0}
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all disabled:opacity-30"
                  data-testid="lightbox-prev-btn"
                >
                  <ChevLeft className="w-5 h-5 text-white" />
                </button>
                <button
                  onClick={() => setLightboxIdx(i => (i + 1) % selectedPhotos.length)}
                  disabled={lightboxIdx === selectedPhotos.length - 1}
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all disabled:opacity-30"
                  data-testid="lightbox-next-btn"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnail strip */}
          {selectedPhotos.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {selectedPhotos.map((p, i) => (
                <button
                  key={p.photo_id}
                  onClick={() => setLightboxIdx(i)}
                  className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    i === lightboxIdx ? "border-primary scale-110" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                  data-testid={`lightbox-thumb-${i}`}
                >
                  <img src={getPhotoUrl(p)} alt={`Thumb ${i}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WoundDetailPage() {
  const { woundId } = useParams();
  const navigate = useNavigate();
  const [wound, setWound] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("evolucoes");
  const [sessionToken, setSessionToken] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  const isdiabetic = wound?.wound_type === "pe_diabetico";
  const isPressureInjury = wound?.wound_type === "lesao_por_pressao";

  const tabs = [
    { id: "evolucoes", label: "Evoluções" },
    { id: "fotos", label: "Fotos" },
    ...(isdiabetic ? [{ id: "wagner", label: "Wagner" }] : []),
    ...(isPressureInjury ? [{ id: "npuap", label: "NPUAP/EPUAP" }] : []),
    { id: "ia", label: "IA" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [woundResp, meResp] = await Promise.all([
          axios.get(`${API}/wounds/${woundId}`, { withCredentials: true }),
          axios.get(`${API}/auth/me`, { withCredentials: true }),
        ]);
        setWound(woundResp.data);
        // Extract session token for photo display
        const cookies = document.cookie.split(';');
        const sessionCookie = cookies.find(c => c.trim().startsWith('session_token='));
        setSessionToken(sessionCookie ? sessionCookie.split('=')[1].trim() : "");
        if (woundResp.data.patient_id) {
          const patResp = await axios.get(`${API}/patients/${woundResp.data.patient_id}`, { withCredentials: true });
          setPatient(patResp.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [woundId]);

  const handleUpdateStatus = async () => {
    try {
      const resp = await axios.put(`${API}/wounds/${woundId}`, { status: newStatus }, { withCredentials: true });
      setWound(prev => ({ ...prev, status: resp.data.status }));
      setShowStatusModal(false);
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return <Layout><div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 rounded-xl bg-white border border-border animate-pulse" />)}</div></Layout>;
  }
  if (!wound) {
    return <Layout><div className="text-center py-16 text-muted-foreground">Lesão não encontrada</div></Layout>;
  }

  const evolutions = wound.evolutions || [];
  const photos = wound.photos || [];

  return (
    <Layout>
      <div data-testid="wound-detail-page" className="space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3 animate-fade-in">
          <button
            onClick={() => patient ? navigate(`/patients/${wound.patient_id}`) : navigate("/patients")}
            className="p-2 rounded-lg hover:bg-secondary transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              {WOUND_LABELS[wound.wound_type] || wound.wound_type}
            </h1>
            {patient && <p className="text-sm text-muted-foreground">Paciente: {patient.name}</p>}
          </div>
        </div>

        {/* Wound info */}
        <div className="bg-white rounded-xl border border-border p-5 animate-fade-in">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm text-muted-foreground">{wound.location}</p>
              {wound.start_date && <p className="text-xs text-muted-foreground mt-0.5">Início: {wound.start_date}</p>}
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full risk-${wound.risk_level || "baixo"}`} data-testid="wound-risk-badge">
                {RISK_LABELS[wound.risk_level] || "Baixo"}
              </span>
              <button
                onClick={() => { setNewStatus(wound.status); setShowStatusModal(true); }}
                className="text-xs text-muted-foreground underline"
              >
                {STATUS_LABELS[wound.status]}
              </button>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 animate-fade-in">
          <button
            onClick={() => navigate(`/evolutions/new?woundId=${woundId}&patientId=${wound.patient_id}`)}
            className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all"
            data-testid="new-evolution-btn"
          >
            <Plus className="w-4 h-4" />
            Nova Evolução
          </button>
          <button
            onClick={() => navigate(`/teleconsultation?woundId=${woundId}&patientId=${wound.patient_id}`)}
            className="flex items-center justify-center gap-2 border border-border bg-white py-3 px-3 rounded-xl text-sm font-medium hover:bg-secondary transition-all"
            title="Interconsulta"
          >
            <Activity className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Interconsulta</span>
          </button>
          <button
            onClick={() => navigate(`/relatorio/${woundId}`)}
            className="flex items-center justify-center gap-2 border border-border bg-white py-3 px-3 rounded-xl text-sm font-medium hover:bg-secondary transition-all"
            title="Exportar PDF"
            data-testid="export-pdf-btn"
          >
            <FileDown className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Exportar PDF</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-border overflow-hidden animate-fade-in">
          <div className="flex border-b border-border overflow-x-auto">
            {tabs.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex-shrink-0 flex-1 py-3 text-sm font-medium transition-all whitespace-nowrap px-3 ${
                  activeTab === id ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`wound-tab-${id}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="p-4">
            {/* Evolutions tab */}
            {activeTab === "evolucoes" && (
              <>
                <AreaTimeline evolutions={wound.evolutions} />
                <div className="mt-3 space-y-3">
                {evolutions.length === 0 ? (
                  <div className="text-center py-8">
                    <Activity className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma evolução registrada</p>
                    <button
                      onClick={() => navigate(`/evolutions/new?woundId=${woundId}&patientId=${wound.patient_id}`)}
                      className="mt-2 text-sm text-primary font-medium hover:underline"
                    >
                      Registrar primeira evolução
                    </button>
                  </div>
                ) : (
                  evolutions.map((evo) => (
                    <div
                      key={evo.evolution_id}
                      className="border border-border/70 rounded-xl p-4 hover:bg-secondary/30 transition-all cursor-pointer"
                      data-testid={`evolution-card-${evo.evolution_id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-foreground">{evo.date}</p>
                        {evo.risk_level && (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full risk-${evo.risk_level}`}>
                            {RISK_LABELS[evo.risk_level]}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        {(evo.length_cm > 0 || evo.width_cm > 0) && (
                          <p>Dimensões: {evo.length_cm}cm × {evo.width_cm}cm × {evo.depth_cm}cm</p>
                        )}
                        {evo.tissue_types?.length > 0 && (
                          <p>Tecidos: {evo.tissue_types.join(", ")}</p>
                        )}
                        {evo.infection_signs?.length > 0 && (
                          <p className="text-destructive font-medium">Infecção: {evo.infection_signs.join(", ")}</p>
                        )}
                        {evo.treatment && <p>Tratamento: {evo.treatment}</p>}
                      </div>
                      {evo.ai_analysis?.nivel_risco && (
                        <div className="mt-2 pt-2 border-t border-border/50 flex items-center gap-1.5">
                          <Brain className="w-3.5 h-3.5 text-primary" />
                          <p className="text-xs text-muted-foreground line-clamp-1">{evo.ai_analysis.resumo_clinico}</p>
                        </div>
                      )}
                      {evo.signature && (
                        <div className="mt-2 pt-2 border-t border-border/50 flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <p className="text-xs text-green-700 font-medium truncate">{evo.signature.signed_by} — {evo.signature.signed_at_brt}</p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              </>
            )}

            {/* Photos tab */}
            {activeTab === "fotos" && (
              <PhotoHistoryTimeline photos={photos} evolutions={evolutions} sessionToken={sessionToken} />
            )}

            {/* Wagner tab - apenas pé diabético */}
            {activeTab === "wagner" && (
              <WagnerTab
                wound={wound}
                onGradeUpdate={(grade) => setWound(prev => ({ ...prev, wagner_grade: grade }))}
              />
            )}

            {/* NPUAP tab - apenas lesão por pressão */}
            {activeTab === "npuap" && (
              <NPUAPTab
                wound={wound}
                onStageUpdate={(stage) => setWound(prev => ({ ...prev, npuap_stage: stage }))}
              />
            )}

            {/* AI tab */}
            {activeTab === "ia" && (
              <div className="space-y-4">
                {evolutions.filter(e => e.ai_analysis?.nivel_risco).length === 0 ? (
                  <div className="text-center py-8">
                    <Brain className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma análise de IA disponível</p>
                    <p className="text-xs text-muted-foreground mt-1">Registre uma evolução e solicite análise de IA</p>
                  </div>
                ) : (
                  evolutions.filter(e => e.ai_analysis?.nivel_risco).map(evo => (
                    <div key={evo.evolution_id} className="border border-border rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Brain className="w-4 h-4 text-primary" />
                          <span className="text-sm font-semibold" style={{ fontFamily: 'Manrope' }}>Análise IA</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{evo.date}</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full risk-${evo.ai_analysis.nivel_risco}`}>
                            {RISK_LABELS[evo.ai_analysis.nivel_risco]}
                          </span>
                        </div>
                      </div>
                      {evo.ai_analysis.bandeiras_vermelhas?.length > 0 && (
                        <div className="bg-destructive/10 rounded-lg p-3 mb-3">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
                            <span className="text-xs font-semibold text-destructive">Bandeiras Vermelhas</span>
                          </div>
                          <ul className="text-xs text-destructive space-y-0.5">
                            {evo.ai_analysis.bandeiras_vermelhas.map((flag, i) => (
                              <li key={i}>• {flag}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p className="text-sm text-foreground mb-2">{evo.ai_analysis.resumo_clinico}</p>
                      {evo.ai_analysis.achados_visuais && (
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mt-2 mb-2">
                          <p className="text-xs font-semibold text-blue-700 mb-1">Achados Visuais (Foto)</p>
                          <p className="text-xs text-blue-900">{evo.ai_analysis.achados_visuais}</p>
                        </div>
                      )}
                      {evo.ai_analysis.sugestao_protocolo && (
                        <div className="bg-primary/5 rounded-lg p-3 mt-2">
                          <p className="text-xs font-semibold text-primary mb-1">Sugestão de Protocolo e Curativo</p>
                          <p className="text-xs text-foreground whitespace-pre-line">{evo.ai_analysis.sugestao_protocolo}</p>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-3 italic">
                        Este é um suporte à decisão clínica, não um diagnóstico. Sempre avalie clinicamente o paciente.
                      </p>
                      {evo.signature && (
                        <div className="mt-4 pt-4 border-t border-border/60 flex items-start gap-2">
                          <svg className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <div>
                            <p className="text-xs font-medium text-green-700">Assinatura Eletrônica Verificada</p>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{evo.signature.signature_block}</p>
                            <p className="text-xs text-muted-foreground/60 mt-0.5">Lei 14.063/2020</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Status update modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 animate-fade-in">
            <h3 className="text-base font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Atualizar Status</h3>
            <select
              value={newStatus}
              onChange={e => setNewStatus(e.target.value)}
              className="w-full h-12 border border-border rounded-lg px-3 text-sm mb-4"
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <div className="flex gap-3">
              <button onClick={() => setShowStatusModal(false)} className="flex-1 py-2.5 border border-border rounded-xl text-sm">Cancelar</button>
              <button onClick={handleUpdateStatus} className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-medium">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
